import { useState } from "react";
import {
  setStatusOverride,
  clearStatusOverride,
  ApiError,
  type AdminAssignment,
} from "../api";

type StatusValue = "open" | "partially_open" | "closed";

/**
 * Notifications tool — the daily-driver status-push UI for a trail.
 *
 * Extracted from the old AdminTrailCard so it can stand alone as a
 * sidebar "tool" in the My Trails dashboard. Select-then-confirm
 * flow: tap a traffic-light, attach an optional note, confirm to
 * push to riders. Co-push to sibling trails that share a status
 * page is opt-in via checkbox. Active override gets a "Clear
 * override" escape hatch.
 */
export function AdminTrailNotifications({
  trail,
  siblings = [],
  onChanged,
}: {
  trail: AdminAssignment;
  siblings?: AdminAssignment[];
  onChanged: () => void;
}) {
  const [pending, setPending] = useState<StatusValue | null>(null);
  const [summary, setSummary] = useState("");
  const [ttlHours, setTtlHours] = useState(4);
  const [submitting, setSubmitting] = useState<"push" | "clear" | null>(null);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(
    null
  );
  const [alsoPushTo, setAlsoPushTo] = useState<Set<string>>(new Set());
  const [feedback, setFeedback] = useState<
    { kind: "ok" | "err"; msg: string } | null
  >(null);

  const currentValue = trail.status.value as StatusValue | "unknown" | null;
  const hasActiveOverride =
    trail.status.source === "admin" && trail.status.expiresAt != null;
  const overrideExpiry =
    hasActiveOverride && trail.status.expiresAt
      ? formatExpiry(trail.status.expiresAt)
      : null;

  function selectPending(value: StatusValue) {
    if (submitting != null) return;
    setPending((cur) => (cur === value ? null : value));
    setFeedback(null);
  }

  function clearPending() {
    setPending(null);
    setSummary("");
    setAlsoPushTo(new Set());
    setFeedback(null);
  }

  function toggleSibling(slug: string) {
    setAlsoPushTo((cur) => {
      const next = new Set(cur);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  }

  async function commitPush() {
    if (pending == null) return;
    const targets = [trail, ...siblings.filter((s) => alsoPushTo.has(s.slug))];
    setSubmitting("push");
    setFeedback(null);
    setProgress(targets.length > 1 ? { done: 0, total: targets.length } : null);
    let pushedTotal = 0;
    const failures: string[] = [];
    let lastExpiry: number | null = null;
    for (let i = 0; i < targets.length; i++) {
      const t = targets[i];
      try {
        const r = await setStatusOverride({
          slug: t.slug,
          status: pending,
          summary: summary.trim() || undefined,
          ttlHours,
        });
        pushedTotal += r.pushed;
        lastExpiry = r.status.expiresAt;
      } catch (err) {
        failures.push(
          `${t.name}: ${err instanceof ApiError ? err.message : "failed"}`
        );
      }
      setProgress(
        targets.length > 1 ? { done: i + 1, total: targets.length } : null
      );
    }
    setProgress(null);
    setSubmitting(null);
    if (failures.length === 0) {
      const recipients =
        pushedTotal === 1 ? "1 rider" : `${pushedTotal} riders`;
      const scope =
        targets.length > 1 ? `${targets.length} trails` : trail.name;
      setFeedback({
        kind: "ok",
        msg: `Pushed ${labelFor(pending)} for ${scope} to ${recipients}. Expires ${formatExpiry(lastExpiry ?? 0)}.`,
      });
      setPending(null);
      setSummary("");
      setAlsoPushTo(new Set());
      onChanged();
    } else {
      setFeedback({
        kind: "err",
        msg: `${failures.length} of ${targets.length} failed. ${failures[0]}`,
      });
      onChanged();
    }
  }

  async function handleClear() {
    if (
      !confirm(
        `Clear your override on ${trail.name}? The automatic check will resume on the next cycle.`
      )
    )
      return;
    setSubmitting("clear");
    setFeedback(null);
    try {
      await clearStatusOverride(trail.slug);
      setFeedback({ kind: "ok", msg: "Override cleared." });
      onChanged();
    } catch (err) {
      setFeedback({
        kind: "err",
        msg: err instanceof ApiError ? err.message : "Couldn't clear the override.",
      });
    } finally {
      setSubmitting(null);
    }
  }

  return (
    <>
      <div className="admin-current-state-banner">
        <div>
          <div className="muted" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em" }}>
            Currently pushing
          </div>
          <CurrentStatusPill value={currentValue} source={trail.status.source} />
        </div>
        {hasActiveOverride && (
          <button
            type="button"
            className="small secondary"
            onClick={handleClear}
            disabled={submitting != null}
          >
            {submitting === "clear" ? "Clearing…" : "Clear override"}
          </button>
        )}
      </div>

      {hasActiveOverride && (
        <div className="muted" style={{ fontSize: 12, margin: "-8px 0 8px" }}>
          Your override expires {overrideExpiry}. After that the automatic
          check takes over again.
        </div>
      )}

      <div className="admin-section">
        <div className="admin-section-heading">
          {pending ? "Confirm the change" : "How is it today?"}
        </div>
        <div className="admin-section-body">
          <div className="admin-trafficlight">
            <TrafficButton
              label="Open"
              tone="open"
              pending={pending === "open"}
              disabled={submitting != null}
              onClick={() => selectPending("open")}
            />
            <TrafficButton
              label="Caution"
              tone="caution"
              pending={pending === "partially_open"}
              disabled={submitting != null}
              onClick={() => selectPending("partially_open")}
            />
            <TrafficButton
              label="Closed"
              tone="closed"
              pending={pending === "closed"}
              disabled={submitting != null}
              onClick={() => selectPending("closed")}
            />
          </div>
          {pending && (
            <>
              <div className="admin-confirm-row">
                <span className="admin-confirm-hint muted">
                  You'll push <strong>{labelFor(pending)}</strong> to riders
                  saved on <strong>{trail.name}</strong>
                  {alsoPushTo.size > 0 && (
                    <>
                      {" + "}
                      <strong>{alsoPushTo.size}</strong>{" "}
                      linked trail{alsoPushTo.size === 1 ? "" : "s"}
                    </>
                  )}
                  .
                </span>
                <div className="row" style={{ gap: 8 }}>
                  <button
                    type="button"
                    className="small secondary"
                    onClick={clearPending}
                    disabled={submitting === "push"}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={commitPush}
                    disabled={submitting === "push"}
                  >
                    {submitting === "push"
                      ? progress
                        ? `Pushing ${progress.done}/${progress.total}…`
                        : "Pushing…"
                      : "Set status & push"}
                  </button>
                </div>
              </div>
              {siblings.length > 0 && (
                <div className="admin-sibling-optin">
                  <div className="admin-sibling-optin-heading muted">
                    Shares a status page with{" "}
                    {siblings.length === 1
                      ? "another of your trails"
                      : `${siblings.length} other trails`}
                    — also push to:
                  </div>
                  <ul>
                    {siblings.map((s) => (
                      <li key={s.slug}>
                        <label className="admin-sibling-row">
                          <input
                            type="checkbox"
                            checked={alsoPushTo.has(s.slug)}
                            onChange={() => toggleSibling(s.slug)}
                            disabled={submitting === "push"}
                          />
                          <span>
                            <strong>{s.name}</strong>
                            <span className="muted" style={{ marginLeft: 6 }}>
                              {[s.region, s.state].filter(Boolean).join(", ") ||
                                "—"}
                            </span>
                          </span>
                        </label>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <div className="admin-section">
        <div className="admin-section-heading">
          Tell riders what's going on (optional)
        </div>
        <div className="admin-section-body">
          <input
            type="text"
            placeholder="e.g. Trails are riding fast and dry — go enjoy them!"
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            maxLength={140}
          />
        </div>
      </div>

      <div className="admin-section">
        <div className="admin-section-heading">Override length</div>
        <div className="admin-section-body">
          <label className="drawer-field-label">
            How long should this override last?
          </label>
          <div className="row" style={{ gap: 8, alignItems: "center" }}>
            <input
              type="number"
              inputMode="numeric"
              min={1}
              max={72}
              value={ttlHours}
              onChange={(e) =>
                setTtlHours(
                  Math.max(
                    1,
                    Math.min(72, parseInt(e.target.value || "4", 10) || 4)
                  )
                )
              }
              style={{ width: 90 }}
            />
            <span className="muted" style={{ fontSize: 13 }}>
              hours (1–72). After this, the automatic check resumes.
            </span>
          </div>
        </div>
      </div>

      {feedback && (
        <div
          className={
            feedback.kind === "ok"
              ? "admin-feedback admin-feedback-ok"
              : "admin-feedback admin-feedback-err"
          }
        >
          {feedback.msg}
        </div>
      )}
    </>
  );
}

function TrafficButton({
  label,
  tone,
  pending,
  disabled,
  onClick,
}: {
  label: string;
  tone: "open" | "caution" | "closed";
  pending: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  const classes = ["traffic-btn", `traffic-btn-${tone}`];
  if (pending) classes.push("pending");
  return (
    <button
      type="button"
      className={classes.join(" ")}
      onClick={onClick}
      disabled={disabled}
      aria-pressed={pending}
    >
      {label}
    </button>
  );
}

function CurrentStatusPill({
  value,
  source,
}: {
  value: "open" | "partially_open" | "closed" | "unknown" | null;
  source: "cron" | "admin" | null;
}) {
  const tone =
    value === "open"
      ? "open"
      : value === "closed"
        ? "closed"
        : value === "partially_open"
          ? "caution"
          : "unknown";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
      <span className={`status-pill status-pill-${tone}`}>{labelFor(value)}</span>
      {source && (
        <span className="muted" style={{ fontSize: 11 }}>
          {source === "admin" ? "you set this" : "auto-checked"}
        </span>
      )}
    </div>
  );
}

function labelFor(
  value: "open" | "partially_open" | "closed" | "unknown" | null
): string {
  return value === "open"
    ? "Open"
    : value === "closed"
      ? "Closed"
      : value === "partially_open"
        ? "Caution"
        : "Unknown";
}

function formatExpiry(epochSeconds: number): string {
  const date = new Date(epochSeconds * 1000);
  const diffMs = epochSeconds * 1000 - Date.now();
  if (diffMs <= 0) return "now";
  const hours = Math.floor(diffMs / 3_600_000);
  const minutes = Math.floor((diffMs % 3_600_000) / 60_000);
  if (hours === 0) return `in ${minutes}m`;
  if (minutes === 0) return `in ${hours}h`;
  if (hours >= 6) {
    return `${date.toLocaleString([], {
      weekday: "short",
      hour: "numeric",
      minute: "2-digit",
    })}`;
  }
  return `in ${hours}h ${minutes}m`;
}
